import { Button } from '@domain-group/suit-theme'

import { generateUrls } from '@hooks/useCampaignQuote'
import useCurrentUser from '@hooks/useCurrentUser'
import type { UseOrderPayload } from '@hooks/useOrder'
import useSelector from '@hooks/useSelector'

import { getCampaignPermissions, getCampaignType } from '@functions/campaign'

import type { AgencyModel, BrandModel, CampaignModel } from '@models/types'

interface OrderNewItemButtonProps {
  callbacks: {
    addNewOrder: UseOrderPayload['callbacks']['addNewOrder'],
  },
  campaignId: number,
  orderLimitReached: boolean,
}

const OrderNewItemButton = (props: OrderNewItemButtonProps) => {
  const {
    callbacks: { addNewOrder }, campaignId, orderLimitReached,
  } = props

  const { currentUser } = useCurrentUser()

  const { agencies, campaigns, brands } = useSelector(reduxState => reduxState.entities)
  const campaign: Partial<CampaignModel> = campaigns && campaignId ? (campaigns[campaignId] ?? {}) : {}
  const campaignPermissions = getCampaignPermissions({
    user: currentUser,
    campaignType: getCampaignType(campaign),
  })

  const userCanOrder = campaignPermissions.orders.manage || false
  const requiresQuote = campaignPermissions.orders.require_quote || false

  const { campaignQuotesIndexUrl } = generateUrls(undefined, {
    prefixRoute: `campaigns/${campaignId}/edit`,
  })

  const agency: Partial<AgencyModel> = agencies && campaign?.agency_id
    ? agencies[campaign.agency_id] : {}

  const brand: Partial<BrandModel> = brands && agency?.brand_id ? brands[agency.brand_id] : {}

  const compliantWithAML = brand?.options?.aml_compliance_enforcement ?? false

  const isPropertyMarketing = campaign?.agency_marketing === false

  const { vendor_aml_complete: vendorAmlComplete } = campaign

  if (!userCanOrder){
    return null
  }

  if (requiresQuote){
    return (
      <Button
        // @ts-expect-error TS2322
        as="a"
        buttonStyle="primaryCreate"
        href={campaignQuotesIndexUrl}
        size="medium"
      >
        Create New Quote
      </Button>
    )
  }

  if (compliantWithAML && isPropertyMarketing && !vendorAmlComplete){
    return (
      <Button
        buttonStyle="primaryCreate"
        disabled
        size="medium"
        width="100%"
        style={{ cursor: 'not-allowed' }}
      >
        Order New Item
      </Button>
    )
  }

  return (
    <Button
      buttonStyle="primaryCreate"
      data-test-id="orderNewItemButton"
      disabled={orderLimitReached}
      onClick={() => addNewOrder()}
      size="medium"
      width="auto"
    >
      {orderLimitReached ? 'Max Orders Reached' : 'Order New Item'}
    </Button>
  )
}

export default OrderNewItemButton
